import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "./utils";

// ── Filter types ────────────────────────────────────────────────────────────

export type GoToFilterType =
  | "all"
  | "by_number"
  | "conversation"
  | "owner_conversation"
  | "by_entity";

export type GoToEntitySide = "from" | "to" | "any";

export interface GoToMessagesParams {
  filter_type: GoToFilterType;
  page?: number;
  pageSize?: number;
  date_from?: string;
  date_to?: string;

  // by_number
  phone_number?: string;

  // conversation
  number_a?: string;
  number_b?: string;

  // owner_conversation
  owner_number?: string;
  counterparty_number?: string;

  // by_entity
  entity_type?: "user" | "customer";
  entity_id?: string;
  side?: GoToEntitySide;
}

// ── Response types ──────────────────────────────────────────────────────────

export interface GoToMessageUser {
  id: string;
  name: string;
}

export interface GoToMessageCustomer {
  id: string;
  long_name: string;
}

export interface GoToMessageDocument {
  id: string;
  filename: string;
  content_type: string;
}

export interface GoToMessageWithRelations {
  id: string;
  created_at: string;
  parent_id: string;
  owner_phone_number: string;
  from_number: string;
  to_number: string;
  direction: "inbound" | "outbound";
  message_timestamp: string;
  message_type: string;
  content_type: string;
  body: string | null;
  document_id: string | null;
  from_customer_id: string | null;
  from_user_id: string | null;
  to_customer_id: string | null;
  to_user_id: string | null;

  FromCustomer: GoToMessageCustomer | null;
  FromUser: GoToMessageUser | null;
  ToCustomer: GoToMessageCustomer | null;
  ToUser: GoToMessageUser | null;
  Document: GoToMessageDocument | null;

  presigned_get_url: string | null | undefined;
}

export interface GoToPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

export interface GoToMessagesResponse {
  data: GoToMessageWithRelations[];
  pagination: GoToPagination;
}

// ── Query param assembly ────────────────────────────────────────────────────

function assembleGoToQueryParams(params: GoToMessagesParams): URLSearchParams {
  const qp = new URLSearchParams();

  qp.set("filter_type", params.filter_type);
  qp.set("page", String(params.page ?? 1));
  qp.set("pageSize", String(params.pageSize ?? 50));

  if (params.date_from) qp.set("date_from", params.date_from);
  if (params.date_to) qp.set("date_to", params.date_to);

  if (params.filter_type === "by_number" && params.phone_number) {
    qp.set("phone_number", params.phone_number);
  }

  if (params.filter_type === "conversation") {
    if (params.number_a) qp.set("number_a", params.number_a);
    if (params.number_b) qp.set("number_b", params.number_b);
  }

  if (params.filter_type === "owner_conversation") {
    if (params.owner_number) qp.set("owner_number", params.owner_number);
    if (params.counterparty_number) qp.set("counterparty_number", params.counterparty_number);
  }

  if (params.filter_type === "by_entity") {
    if (params.entity_type) qp.set("entity_type", params.entity_type);
    if (params.entity_id) qp.set("entity_id", params.entity_id);
    if (params.side) qp.set("side", params.side);
  }

  return qp;
}

// ── Fetch + hook ────────────────────────────────────────────────────────────

export function fetchGoToMessages(params: GoToMessagesParams) {
  const qp = assembleGoToQueryParams(params);
  return fetchApiGet<GoToMessagesResponse>("chats/goto/messages", qp);
}

export function useGoToMessages(params: GoToMessagesParams) {
  const qp = assembleGoToQueryParams(params);

  return useQuery({
    queryKey: ["goto_messages", qp.toString()],
    queryFn: () => fetchGoToMessages(params),
    retry: 1,
    keepPreviousData: true,
  });
}
